import { ComponentType, lazy } from 'react';

const RELOAD_KEY = 'chunk-reload-at';

/**
 * Reloads the page to pick up a fresh index.html after a lazy chunk failed to load
 * (a deploy replaced the hashed files, or flaky in-app browser network).
 * Returns false when it already reloaded in the last 10s (or storage is blocked),
 * so callers let the error propagate instead of looping.
 */
export const reloadForStaleChunk = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 10_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

/**
 * `lazy()` for a named export. iOS Safari sometimes resolves a failed `import()` with
 * `undefined` instead of rejecting, which skips `vite:preloadError`; treat it as a stale chunk.
 */
export const lazyNamed = <M extends Record<K, ComponentType<any>>, K extends keyof M>(loader: () => Promise<M>, name: K) =>
  lazy(async (): Promise<{ default: M[K] }> => {
    const module = await loader();
    if (!module) {
      // Never resolve while the page reloads, so no error UI flashes.
      if (reloadForStaleChunk()) return new Promise<never>(() => {});
      throw new TypeError('Importing a module script failed (module resolved undefined).');
    }
    return { default: module[name] };
  });
